import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, TabContainer, TabContent, TabPane, Nav } from 'react-bootstrap';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';

import { setUniqueCookie, hasPermissions } from '../../utils';
import { authApi } from '../../api';

import LoaderButton from '../common/LoaderButton';

import {
  SILA_MARKET_PLACE_URL,
  SILA_SECURITY_REQUIREMENTS_URL,
  START_SILA_JOURNEY_URL,
  EMAIL_SUPPORT_URL,
} from '../../constants';

import { useAuth, useAlerts } from '../context';

function OnboardingBanner() {
  const { env, user } = useAuth();
  const { setAlert } = useAlerts();
  const [key, setKey] = useState(1);
  const [isSending, setIsSending] = useState(false);
  const [completedAllSteps, setCompletedAllSteps] = useState(false);
  const [disabledResendButton, setDisableResendButton] = useState(true);
  const location = useLocation();
  const [startSilaJourney, setStartSilaJourney] = useState(
    !!Cookies.get(`start_sila_journey_${user.entity.handle}`)
  );
  const [exploreMarketPlace, setExploreMarketPlace] = useState(
    !!Cookies.get(`sila_console_marketplace_${user.entity.handle}`)
  );
  const [newWelcomePanel, setNewWelcomePanel] = useState(
    !Cookies.get(`sila_console_newwelcome_closed_${user.entity.handle}`)
  );
  const [securityRequirements, setSecurityRequirements] = useState(
    !!Cookies.get(`sila_console_security_requirements_${user.entity.handle}`)
  );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canInvite = hasPermissions(user, 'members', 'Write');
  let steps = [1, 3, 4, 5, 6];
  if (canInvite) steps = [1, 2, 3, 4, 5, 6];

  const handleAction = (step) => {
    switch (step) {
      case 2:
        navigate({ pathname: '/settings/members/new', state: { from: location.pathname } });
        setUniqueCookie('sila_console_invited_team', user.entity.handle);
        break;
      case 3:
        setStartSilaJourney(true);
        setUniqueCookie('start_sila_journey', user.entity.handle);
        window.open(START_SILA_JOURNEY_URL);
        break;
      case 4:
        setSecurityRequirements(true);
        setUniqueCookie('sila_console_security_requirements', user.entity.handle);
        window.open(SILA_SECURITY_REQUIREMENTS_URL);
        break;
      case 5:
        setUniqueCookie('create_app', user.entity.handle);
        navigate({ pathname: '/app/new', state: { from: location.pathname } });
        break;
      case 6:
        setUniqueCookie('sila_console_marketplace', user.entity.handle);
        setExploreMarketPlace(true);
        window.open(SILA_MARKET_PLACE_URL);
        break;
      default:
    }
  };

  const isStepComplete = (step) => {
    if (step === 1) return user.confirmed_at;
    if (step === 2) return user.hasMembers;
    if (step === 3) return startSilaJourney;
    if (step === 4) return securityRequirements;
    if (step === 5) return user.hasApps;
    if (step === 6) return exploreMarketPlace;
    return false;
  };

  const checkCompletes = () => {
    let nextTabKey = null;
    const incompleteStep = steps.find((step) => !isStepComplete(step) && !completedAllSteps);

    if (incompleteStep) {
      nextTabKey = incompleteStep;
    } else {
      setCompletedAllSteps(true);
      nextTabKey = 7;
    }

    if (nextTabKey !== key) {
      setKey(nextTabKey);
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    setUniqueCookie('sila_console_newwelcome_closed', user.entity.handle);
    setNewWelcomePanel(false);
  };

  const resendVerificationEmail = async () => {
    try {
      setIsSending(true);
      await authApi.resendVerificationEmail(user.entity.handle, user.entity.emails['0'].email);
      setAlert({
        message: t('resendVerfication.message', { email: user.entity.emails['0'].email }),
        variant: 'success',
      });
      checkCompletes();
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
    }
    setDisableResendButton(true);
    setIsSending(false);
  };

  // load data for disabling resend email buttin
  useEffect(() => {
    let timeoutId = null;
    let isMounted = true;

    const fetchData = async () => {
      if (!user.confirmed_at) {
        const response = await authApi.resendVerificationEmailResendDate(user.entity.handle);
        if (isMounted && response) {
          const resendAt = new Date(response);
          const currentTime = new Date();

          // Check if the current time is before the resend time
          if (currentTime < resendAt) {
            const msUntilEnable = resendAt.getTime() - currentTime.getTime();
            timeoutId = setTimeout(() => {
              setDisableResendButton(false);
            }, msUntilEnable);
          } else if (isMounted) {
            setDisableResendButton(false);
          }
        } else if (isMounted) {
          setDisableResendButton(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [disabledResendButton]);

  useEffect(() => {
    if (newWelcomePanel) checkCompletes();
  }, [startSilaJourney, exploreMarketPlace, securityRequirements]);

  return newWelcomePanel ? (
    <Card className="welcome-banner flex-fill mb-5">
      <Card.Body as={Row} className="p-4">
        <div className="d-flex justify-content-between">
          <div>
            <h2 className="mb-3">{t('welcome.getStarted.title')}</h2>
            <p className="text-lg text-info mb-5">{t('welcome.getStarted.description')}</p>
          </div>

          {completedAllSteps && (
            <button type="submit" className="text-info btn-close text-lg" onClick={handleClick}>
              <span className="visually-hidden">{t('common:buttons.close')}</span>
            </button>
          )}
        </div>

        <TabContainer activeKey={key} onSelect={(k) => setKey(k)}>
          <Col md="12" lg="5">
            <Nav className="step-list flex-column">
              {steps.map((step) => {
                return (
                  <Nav.Link
                    key={step}
                    eventKey={step}
                    className={`rounded d-flex${isStepComplete(step) ? ' complete' : ''}`}
                  >
                    {t(`welcome.getStarted.actionKey${step}`)}
                  </Nav.Link>
                );
              })}
            </Nav>
          </Col>
          <Col className="mt-4 mt-mb-0 mt-lg-0" md="12" lg="7">
            <TabContent className="border rounded p-4 h-100">
              {steps.map((step) => (
                <TabPane key={step} eventKey={step}>
                  <h4 className="mb-3 text-xl">{t(`welcome.getStarted.actionKey${step}`)}</h4>
                  {step === 5 ? (
                    <p className="text-lg text-info">
                      <Trans i18nKey="welcome.getStarted.actionKey5Content">
                        Once you&apos;ve created your app, install our
                        <a
                          href="https://github.com/Sila-Money/sila-postman-auth"
                          target="_blank"
                          rel="noreferrer"
                          className="text-decoration-none"
                        >
                          Postman Collection
                        </a>
                        and start developing today.
                      </Trans>
                      <p className="mt-3"> {t('applications.empty.messages.notConfirmed')}</p>
                    </p>
                  ) : (
                    <p className="text-lg text-info">
                      {step === 1 && user.confirmed_at
                        ? t('account.confirm.user')
                        : t(`welcome.getStarted.actionKey${step}Content`)}
                    </p>
                  )}
                  {step === 1 ? (
                    <LoaderButton
                      className="text-sm text-uppercase mt-3"
                      variant="primary"
                      type="button"
                      onClick={resendVerificationEmail}
                      loading={isSending}
                      disabled={disabledResendButton}
                      loadingLabel={t('common:status.sending')}
                    >
                      {t('resendVerfication.button')}
                    </LoaderButton>
                  ) : (
                    <Button
                      variant="primary"
                      className="text-sm text-uppercase mt-3"
                      onClick={() => handleAction(step)}
                      disabled={step === 5 && (!user.confirmed_at || env === 'prod')}
                    >
                      {t(`welcome.getStarted.actionKey${step}Button`)}
                    </Button>
                  )}
                </TabPane>
              ))}
              <TabPane eventKey={7} key={7}>
                <h4 className="mb-3 text-xl">{t('welcome.getStarted.congratsHeading')}</h4>
                <p className="text-lg text-info">{t('welcome.getStarted.congratsContent')}</p>
                <Button
                  variant="primary"
                  className="text-sm text-uppercase mt-3"
                  href={EMAIL_SUPPORT_URL}
                  target="_blank"
                >
                  {t('welcome.getStarted.congratsButton')}
                </Button>
              </TabPane>
            </TabContent>
          </Col>
        </TabContainer>
      </Card.Body>
    </Card>
  ) : null;
}

export default OnboardingBanner;
